import React from 'react';
import { APP_VERSION, isNewer } from '../appVersion';
import useReleaseNotes from '../Hooks/useReleaseNotes';
import useServiceWorkerUpdate from '../Hooks/useServiceWorkerUpdate';

/**
 * Non-blocking toast shown when a new build is waiting. "Update now" activates
 * the waiting service worker and reloads this tab; "Later" hides the toast until
 * the app is next foregrounded. When the backend marks a release newer than this
 * build as breaking, the toast cannot be dismissed.
 */
export default function UpdatePrompt() {
  const { needRefresh, activatedElsewhere, dismissed, update, dismiss } = useServiceWorkerUpdate();
  const { minSupported } = useReleaseNotes({ enabled: needRefresh });
  const [applying, setApplying] = React.useState(false);

  const forced = Boolean(minSupported) && isNewer(minSupported, APP_VERSION);

  if (!needRefresh || (dismissed && !forced)) return null;

  const message = activatedElsewhere
    ? 'CodeSoft was updated in another tab. Reload to finish updating.'
    : forced
      ? 'A required update to CodeSoft is ready. Update to keep working.'
      : 'A new version of CodeSoft is available.';

  const handleUpdate = () => {
    setApplying(true);
    update();
  };

  return (
    <div className="sw-update-notice" role="status" aria-live="polite" data-testid="update-prompt">
      <span className="sw-update-notice__message">{message}</span>
      <div className="sw-update-notice__actions">
        <button
          type="button"
          onClick={handleUpdate}
          disabled={applying}
          className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-70"
        >
          {applying ? 'Updating…' : activatedElsewhere ? 'Reload' : 'Update now'}
        </button>
        {!forced && (
          <button
            type="button"
            onClick={dismiss}
            className="rounded-full px-3 py-1 text-sm font-medium text-gray-200 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
          >
            Later
          </button>
        )}
      </div>
    </div>
  );
}
